import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth-context";
import { OwnerAuthProvider, useOwnerAuth } from "@/lib/owner-auth-context";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import QROptin from "@/pages/QROptin";

import DashboardLayout from "@/layouts/DashboardLayout";
import Dashboard from "@/pages/dashboard/Dashboard";
import Customers from "@/pages/dashboard/Customers";
import CustomerDetail from "@/pages/dashboard/CustomerDetail";
import QRLoyalty from "@/pages/dashboard/QRLoyalty";
import Products from "@/pages/dashboard/Products";
import Rewards from "@/pages/dashboard/Rewards";
import WACampaigns from "@/pages/dashboard/WACampaigns";
import RepeatOrder from "@/pages/dashboard/RepeatOrder";
import Referral from "@/pages/dashboard/Referral";
import Membership from "@/pages/dashboard/Membership";
import Reseller from "@/pages/dashboard/Reseller";
import Profit from "@/pages/dashboard/Profit";
import SKUAnalytics from "@/pages/dashboard/SKUAnalytics";
import AIAssistant from "@/pages/dashboard/AIAssistant";
import Benchmark from "@/pages/dashboard/Benchmark";
import Orders from "@/pages/dashboard/Orders";
import OrderImport from "@/pages/dashboard/OrderImport";
import Settings from "@/pages/dashboard/Settings";
import Billing from "@/pages/dashboard/Billing";
import TeamAccess from "@/pages/dashboard/TeamAccess";
import WAIntegration from "@/pages/dashboard/WAIntegration";
import AISegmentation from "@/pages/dashboard/AISegmentation";
import StaffLogin from "@/pages/StaffLogin";

import OwnerLayout from "@/layouts/OwnerLayout";
import OwnerLogin from "@/pages/owner/OwnerLogin";
import OwnerOverview from "@/pages/owner/OwnerOverview";
import OwnerMerchants from "@/pages/owner/OwnerMerchants";
import OwnerAudit from "@/pages/owner/OwnerAudit";
import OwnerFlags from "@/pages/owner/OwnerFlags";
import OwnerSecurity from "@/pages/owner/OwnerSecurity";
import OwnerCMS from "@/pages/owner/OwnerCMS";

const ProtectedRoute = ({ children }) => {
  const { merchant, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Memuat...</div>;
  if (!merchant) return <Navigate to="/login" replace />;
  if (!merchant.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
};

const OnboardingGate = ({ children }) => {
  const { merchant, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Memuat...</div>;
  if (!merchant) return <Navigate to="/login" replace />;
  if (merchant.onboarded) return <Navigate to="/dashboard" replace />;
  return children;
};

const OwnerProtected = ({ children }) => {
  const { owner, loading } = useOwnerAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500 bg-gray-950">Memuat...</div>;
  if (!owner) return <Navigate to="/owner/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <OwnerAuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/qr/:campaignId" element={<QROptin />} />
              <Route path="/staff-login" element={<StaffLogin />} />
              <Route path="/onboarding" element={<OnboardingGate><Onboarding /></OnboardingGate>} />

              <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:id" element={<CustomerDetail />} />
                <Route path="qr-loyalty" element={<QRLoyalty />} />
                <Route path="products" element={<Products />} />
                <Route path="rewards" element={<Rewards />} />
                <Route path="campaigns" element={<WACampaigns />} />
                <Route path="repeat-order" element={<RepeatOrder />} />
                <Route path="referral" element={<Referral />} />
                <Route path="membership" element={<Membership />} />
                <Route path="reseller" element={<Reseller />} />
                <Route path="profit" element={<Profit />} />
                <Route path="sku" element={<SKUAnalytics />} />
                <Route path="ai" element={<AIAssistant />} />
                <Route path="benchmark" element={<Benchmark />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/import" element={<OrderImport />} />
                <Route path="settings" element={<Settings />} />
                <Route path="billing" element={<Billing />} />
                <Route path="team" element={<TeamAccess />} />
                <Route path="wa-integration" element={<WAIntegration />} />
                <Route path="segmentation" element={<AISegmentation />} />
              </Route>

              <Route path="/owner/login" element={<OwnerLogin />} />
              <Route path="/owner" element={<OwnerProtected><OwnerLayout /></OwnerProtected>}>
                <Route index element={<OwnerOverview />} />
                <Route path="merchants" element={<OwnerMerchants />} />
                <Route path="audit" element={<OwnerAudit />} />
                <Route path="flags" element={<OwnerFlags />} />
                <Route path="security" element={<OwnerSecurity />} />
                <Route path="cms" element={<OwnerCMS />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </OwnerAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
